import { useCallback, useEffect } from "react";

import { useAppDispatch, useAppSelector } from "../Store/Hooks";
import {
  fetchMetricsOrgConfiguration,
  updateMetricsOrgConfiguration,
} from "../Store/Reducers/MetricsSlice";

type OrgConfigBlob = Record<string, unknown>;

/**
 * Resolve use_synthetic_data from the org config blob.
 * Canonical: config.use_synthetic_data
 * Legacy FE writes: config.config_override.use_synthetic_data
 */
export function resolveUseSyntheticData(
  orgConfigurationData: Record<string, unknown> | null | undefined
): boolean {
  if (!orgConfigurationData || typeof orgConfigurationData !== "object") {
    return false;
  }
  const config =
    (orgConfigurationData.config as OrgConfigBlob | undefined) || {};
  if (typeof config.use_synthetic_data === "boolean") {
    return config.use_synthetic_data;
  }
  const nested = config.config_override as OrgConfigBlob | undefined;
  if (typeof nested?.use_synthetic_data === "boolean") {
    return nested.use_synthetic_data;
  }
  // Pre-rename response shape (should not appear from current API).
  const legacyTop = orgConfigurationData.config_override as
    | OrgConfigBlob
    | undefined;
  if (typeof legacyTop?.use_synthetic_data === "boolean") {
    return legacyTop.use_synthetic_data;
  }
  return false;
}

function categoriesFromOrgConfig(
  orgConfigurationData: Record<string, unknown> | null | undefined
): Array<{
  name: string;
  weight: number;
  enabled: boolean;
  metrics: Array<{
    name: string;
    weight: number;
    enabled: boolean;
    cadence?: string;
  }>;
}> {
  if (!orgConfigurationData) return [];
  const config =
    (orgConfigurationData.config as OrgConfigBlob | undefined) || {};
  const nested = config.config_override as OrgConfigBlob | undefined;
  const candidates = [
    nested?.categories,
    (orgConfigurationData.config_override as OrgConfigBlob | undefined)
      ?.categories,
    config.categories,
  ];
  for (const categories of candidates) {
    if (Array.isArray(categories) && categories.length > 0) {
      return categories as Array<{
        name: string;
        weight: number;
        enabled: boolean;
        metrics: Array<{
          name: string;
          weight: number;
          enabled: boolean;
          cadence?: string;
        }>;
      }>;
    }
  }
  return [];
}

/**
 * Org-level synthetic vs live metrics toggle
 * (`org_metric_configurations.config.use_synthetic_data`).
 *
 * Always On / Hawkeye digests read this via get_org_data_mode().
 */
export function useOrgSyntheticDataToggle() {
  const dispatch = useAppDispatch();
  const {
    data: orgConfigurationData,
    loading: orgConfigLoading,
    success: orgConfigSuccess,
    error: orgConfigError,
  } = useAppSelector(state => state.metrics.orgConfiguration);
  const { loading: orgConfigUpdateLoading } = useAppSelector(
    state => state.metrics.orgConfigurationUpdate
  );

  const useSyntheticData = resolveUseSyntheticData(
    orgConfigurationData as Record<string, unknown> | null
  );

  // Fetch once. A successful response may still have `configuration: null`
  // (no org config yet) — do not treat that as "missing" and refetch forever.
  useEffect(() => {
    // A 200 with `configuration: null` is a completed empty result, not "not
    // loaded". Treating null as missing retried forever on Always On Channel.
    if (orgConfigLoading || orgConfigSuccess || orgConfigError) return;
    void dispatch(fetchMetricsOrgConfiguration());
  }, [dispatch, orgConfigLoading, orgConfigSuccess, orgConfigError]);

  const setUseSyntheticData = useCallback(
    async (checked: boolean): Promise<boolean> => {
      const configurationId = orgConfigurationData?.id as string | undefined;
      if (!configurationId) {
        await dispatch(fetchMetricsOrgConfiguration());
        return false;
      }
      const existingConfig = ((orgConfigurationData?.config as
        | OrgConfigBlob
        | undefined) || {}) as OrgConfigBlob;
      const existingNested =
        (existingConfig.config_override as OrgConfigBlob | undefined) || {};
      const categories = categoriesFromOrgConfig(
        orgConfigurationData as Record<string, unknown>
      );

      const result = await dispatch(
        updateMetricsOrgConfiguration({
          configuration_id: configurationId,
          template_name:
            (orgConfigurationData?.template_name as string) || "default_v3",
          config_version: Number(orgConfigurationData?.config_version) || 1,
          config: {
            ...existingConfig,
            is_master: Boolean(
              existingConfig.is_master ?? orgConfigurationData?.is_master
            ),
            silent: true,
            // Canonical path Always On / backend get_org_data_mode reads.
            use_synthetic_data: checked,
            // Keep nested copy for older UI readers until fully migrated.
            config_override: {
              ...existingNested,
              use_synthetic_data: checked,
              categories:
                categories.length > 0
                  ? categories
                  : Array.isArray(existingNested.categories)
                    ? (existingNested.categories as typeof categories)
                    : [],
            },
          },
        })
      );
      return updateMetricsOrgConfiguration.fulfilled.match(result);
    },
    [dispatch, orgConfigurationData]
  );

  const dataModeKey = useSyntheticData ? "synthetic" : "live";

  return {
    useSyntheticData,
    dataModeKey,
    setUseSyntheticData,
    loading: orgConfigLoading || orgConfigUpdateLoading,
    disabled:
      orgConfigLoading || orgConfigUpdateLoading || !orgConfigurationData?.id,
  };
}
